import { useEffect, useRef, useState } from "react";
import { BROWSER_STORAGE_KEYS } from "../browser-storage";
import { useBrowserStorageConsent } from "../context/browser-storage-consent-context";
import { useDisciplio } from "../context/disciplio-context";
import { useI18n } from "../context/i18n-context";
import {
  browserNotificationState,
  currentBrowserPushSubscription,
  needsAppleHomeScreenInstall,
  supportsBrowserPush,
} from "./browser-notifications";
import { enableBrowserPushSetup } from "./browser-push-setup";
import { Presence, presenceStateProps, usePresenceState } from "./ui/presence";

const DISMISS_KEY = BROWSER_STORAGE_KEYS.notificationPromptDismissed;
const PROMPT_DELAY_MS = 1600;

function accountStorageKey(email: string) {
  return email.trim().toLowerCase();
}

function readDismissedFromStorage(storage: Storage, accountKey: string) {
  const raw = storage.getItem(DISMISS_KEY);
  if (raw === "1") return true;
  if (!raw) return false;
  try {
    const dismissed = JSON.parse(raw) as Record<string, unknown>;
    return dismissed[accountKey] === true;
  } catch {
    return false;
  }
}

function readDismissed(accountEmail: string, allowOptionalStorage: boolean) {
  if (typeof window === "undefined") return false;
  const accountKey = accountStorageKey(accountEmail);
  if (!accountKey) return false;
  try {
    if (readDismissedFromStorage(sessionStorage, accountKey)) return true;
    if (allowOptionalStorage && readDismissedFromStorage(localStorage, accountKey)) return true;
  } catch {
    return false;
  }
  return false;
}

function storeDismissed(accountEmail: string, allowOptionalStorage: boolean) {
  const accountKey = accountStorageKey(accountEmail);
  if (!accountKey) return;
  const store = (storage: Storage) => {
    const raw = storage.getItem(DISMISS_KEY);
    let dismissed: Record<string, unknown> = {};
    if (raw && raw !== "1") {
      try {
        const parsed = JSON.parse(raw) as unknown;
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
          dismissed = parsed as Record<string, unknown>;
        }
      } catch {
        dismissed = {};
      }
    }
    dismissed[accountKey] = true;
    storage.setItem(DISMISS_KEY, JSON.stringify(dismissed));
  };
  try {
    store(sessionStorage);
    if (allowOptionalStorage) store(localStorage);
  } catch {
    // Prompt dismissal is best-effort and must not block the app.
  }
}

function shouldOfferPrompt() {
  if (!supportsBrowserPush() || needsAppleHomeScreenInstall()) return false;
  if (browserNotificationState() !== "default") return false;
  return true;
}

export function NotificationPermissionPrompt() {
  const { t } = useI18n();
  const { state, dailyItemCreationRevision } = useDisciplio();
  const { isOptionalStorageEnabled } = useBrowserStorageConsent();
  const allowOptionalStorage = isOptionalStorageEnabled;
  const accountEmail = state.profile.email;
  const promptAttemptedRef = useRef(false);
  const timerRef = useRef<number | null>(null);
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const remindersEnabled = state.settings.dailyGoalNotificationsEnabled;

  useEffect(() => {
    if (
      promptAttemptedRef.current ||
      dailyItemCreationRevision < 1 ||
      !accountStorageKey(accountEmail) ||
      !remindersEnabled ||
      !shouldOfferPrompt() ||
      readDismissed(accountEmail, allowOptionalStorage)
    ) return;

    promptAttemptedRef.current = true;
    timerRef.current = window.setTimeout(() => {
      void (async () => {
        const existing = await currentBrowserPushSubscription().catch(() => null);
        if (existing) return;
        if (!shouldOfferPrompt() || readDismissed(accountEmail, allowOptionalStorage)) return;
        setVisible(true);
      })();
    }, PROMPT_DELAY_MS);
  }, [accountEmail, allowOptionalStorage, dailyItemCreationRevision, remindersEnabled]);

  useEffect(() => () => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
  }, []);

  async function enable() {
    setBusy(true);
    setError("");
    const result = await enableBrowserPushSetup();
    setBusy(false);
    if (result.ok) {
      storeDismissed(accountEmail, allowOptionalStorage);
      setVisible(false);
      return;
    }
    if (result.code === "permission_denied") {
      storeDismissed(accountEmail, allowOptionalStorage);
      setVisible(false);
      return;
    }
    if (result.code === "config_unavailable") {
      // Permission may still have been granted; stop prompting on this device.
      if (browserNotificationState() !== "default") {
        storeDismissed(accountEmail, allowOptionalStorage);
        setVisible(false);
        return;
      }
      setError(t("notifications.promptUnavailable"));
      return;
    }
    setError(t("notifications.promptFailed"));
  }

  function dismiss() {
    storeDismissed(accountEmail, allowOptionalStorage);
    setVisible(false);
  }

  return (
    <Presence open={visible} exitDurationMs={180}>
      <NotificationPermissionPromptCard
        busy={busy}
        error={error}
        onEnable={() => void enable()}
        onDismiss={dismiss}
      />
    </Presence>
  );
}

function NotificationPermissionPromptCard({
  busy,
  error,
  onEnable,
  onDismiss,
}: {
  busy: boolean;
  error: string;
  onEnable: () => void;
  onDismiss: () => void;
}) {
  const { t } = useI18n();
  const presenceState = usePresenceState();
  return (
    <aside
      className="notification-permission-prompt"
      role="dialog"
      aria-labelledby="notification-permission-prompt-title"
      aria-describedby="notification-permission-prompt-copy"
      {...presenceStateProps(presenceState)}
    >
      <div className="notification-permission-prompt__copy">
        <strong id="notification-permission-prompt-title">{t("notifications.promptTitle")}</strong>
        <p id="notification-permission-prompt-copy">{t("notifications.promptBody")}</p>
        {error ? <p className="notification-permission-prompt__error">{error}</p> : null}
      </div>
      <div className="notification-permission-prompt__actions">
        <button type="button" className="btn btn--ghost btn--sm" onClick={onDismiss} disabled={busy}>
          {t("notifications.promptLater")}
        </button>
        <button type="button" className="btn btn--primary btn--sm" onClick={onEnable} disabled={busy}>
          {busy ? t("notifications.promptEnabling") : t("notifications.promptEnable")}
        </button>
      </div>
    </aside>
  );
}
